/**
 * ページの見出し・空の画面・骨組み（読み込み中）。
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useI18n } from "../useI18n.js";
import { consumeHeadingFocus, useShell } from "./shell.js";
import { Icon } from "./Icon.js";
import { parseRoute, routeGroupKey } from "../routes.js";
import { useMe } from "../useMe.js";

/**
 * ページ見出し + 主操作。スマホでは上部に固定（高さ 56px）し、検索アイコンを添える。
 * h1 は1ページに1つ。画面を移ったときはここへフォーカスを移す（App が #page-title を探す）。
 * h1 の上に、縦メニューのグループ名（業務／記録・台帳／管理 など）を小さな札で添える。
 * 画面を移ると PageHeader は作り直されるので、表示した時点のハッシュで決めてよい。
 */
export function PageHeader({ title, actions, intro }: { title: ReactNode; actions?: ReactNode; intro?: ReactNode }) {
  const { t } = useI18n();
  const { openSearch } = useShell();
  const me = useMe();
  const [route] = useState(() => parseRoute(location.hash));
  const eyebrow = routeGroupKey(route, me);
  const h1 = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (consumeHeadingFocus()) h1.current?.focus({ preventScroll: true });
  }, []);
  return (
    <>
      <header className="page-header">
        <div className="page-title">
          {eyebrow && <p className="eyebrow">{t(eyebrow)}</p>}
          <h1 id="page-title" tabIndex={-1} ref={h1}>{title}</h1>
        </div>
        <div className="page-actions">
          {actions}
          <button type="button" className="icon-btn search-trigger" onClick={openSearch} aria-label={t("search.open")}>
            <Icon name="search" />
          </button>
        </div>
      </header>
      {intro && <p className="page-intro">{intro}</p>}
    </>
  );
}

/** 空の画面は次の一歩を示す（「申請はまだありません」+「新しく申請する」）。 */
export function EmptyState({ title, body, action }: { title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {/* 見出しにはしない（置かれる場所によって h2 を飛ばした h3 になり、見出しの階層が崩れるため） */}
      <p className="empty-title">{title}</p>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

/** 読み込み中の骨組み。真っ白や「読み込み中…」だけにしない。動かさない。 */
export function Skeleton({ rows = 4, title = false, label }: { rows?: number; title?: boolean; label?: string }) {
  const { t } = useI18n();
  return (
    <div className="skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">{label ?? t("common.loading")}</span>
      {title && <div className="sk title" />}
      {Array.from({ length: rows }, (_, i) => <div key={i} className="sk row" />)}
    </div>
  );
}

/** ページ全体の骨組み（画面の分割読み込み中）。 */
export function PageSkeleton() {
  return (
    <div className="stack" style={{ paddingTop: "var(--s5)" }}>
      <Skeleton title rows={5} />
    </div>
  );
}
